import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck2, MapPin } from 'lucide-react';
import { Img, Section, Shimmer } from '@/components/ui';
import { usePastEvents } from '@/hooks/useCatalog';
import { cleanLocationText } from '@/utils/cleanLocationText';
import { attributeMap, parseMaybeJSON } from '@/lib/catalog';

/**
 * "Events we've hosted" — real social proof.
 *
 * This replaced a marquee of ten invented testimonials attached to stock
 * avatars, half of which were about a different product. Every card here is an
 * event that actually happened on the platform, with its real poster, date and
 * venue, so nothing has to be made up and nothing goes stale.
 */

const monthYear = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const venueOf = (event) => {
  const attrs = attributeMap(event.attributes);
  const location = parseMaybeJSON(attrs.event_location);
  return cleanLocationText(location?.address) || event.providerName || '';
};

const PastEventCard = ({ event, featured, index }) => (
  // CSS stagger rather than a scroll-triggered JS one: a card that starts at
  // opacity 0 and waits for an observer stays blank if anything goes wrong,
  // and this section is the site's social proof — it has to render.
  //
  // The image FILLS the card (absolute inset-0) instead of carrying its own
  // aspect ratio.
  //
  // The lead tile sizes itself by aspect ratio rather than `h-full` +
  // `aspect-auto`. It spans a 2x2 area, and asking it to take its height from
  // the grid was circular: the rows size to their content, the tile's only
  // content is absolutely positioned and so contributes no height, and the
  // card collapsed to roughly one row with a band of white under it. Two 3:4
  // cards plus the gap between them is ~3:4 again, so the same ratio fills the
  // 2x2 area without depending on the grid to resolve it.
  <div
    className={`animate-floatUp ${featured ? 'col-span-2 sm:row-span-2' : ''}`}
    style={{ animationDelay: `${Math.min(index, 6) * 50}ms` }}
  >
    <div
      className={`group relative overflow-hidden rounded-2xl bg-ink-900 shadow-card transition-shadow hover:shadow-cardHover ${
        featured ? 'aspect-[4/3] sm:aspect-[3/4]' : 'h-full aspect-[3/4]'
      }`}
    >
      <Img
        src={event.thumbnail}
        alt={event.name}
        className="absolute inset-0 h-full w-full"
        imgClassName="transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

      <div className={`absolute inset-x-0 bottom-0 ${featured ? 'p-4 sm:p-6' : 'p-3.5'}`}>
        <h3
          className={`line-clamp-2 font-bold leading-tight text-white drop-shadow ${
            featured ? 'text-lg md:text-2xl' : 'text-sm'
          }`}
        >
          {event.name}
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/90">
          <span className="inline-flex items-center gap-1">
            <CalendarCheck2 className="h-3 w-3" aria-hidden="true" />
            {monthYear(event.eventEndAt)}
          </span>
          {venueOf(event) && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin className="h-3 w-3 flex-none" aria-hidden="true" />
              <span className="line-clamp-1">{venueOf(event)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

const PastEvents = () => {
  const { pastEvents, loading } = usePastEvents();

  // A wall of one is not a wall — say nothing rather than look empty.
  if (!loading && pastEvents.length < 2) return null;

  // The lead tile occupies four cells, so 13 with it and 12 without both fill
  // complete rows of four rather than leaving an orphan card on the last row.
  // Sales and attendance figures are deliberately kept off the public site.
  return (
    <Section
      title="Events we've hosted"
      subtitle={loading ? 'Loading…' : 'Moments brought to life on LocalKonnect'}
      className="bg-white"
      action={
        <Link to="/" className="lk-focus inline-flex min-h-[44px] items-center rounded text-sm font-semibold text-brand-secondary hover:underline">
          See what's on
        </Link>
      }
    >
      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Shimmer key={i} className="aspect-[3/4] rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          {pastEvents.slice(0, pastEvents[0]?.isFeatured ? 13 : 12).map((event, i) => (
            <PastEventCard
              key={event.id}
              event={event}
              // The admin-featured event leads the wall at double size.
              featured={i === 0 && event.isFeatured}
              index={i}
            />
          ))}
        </div>
      )}
    </Section>
  );
};

export default PastEvents;
